import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { UserMinus } from '@styled-icons/fa-solid/UserMinus';
import { toast } from 'react-toastify';
import { device } from '../../constants/device-sizes';
import { getBlockList, removeFromBlockList } from '../../utils/localstorage';
import TitleText from './TitleText';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 1rem;
  right: 1rem;
  margin: 1rem 1rem;
  font-size: 2rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  :hover {
    color: red;
  }
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 1rem;
  @media ${device.tablet} {
    width: 60%;
    position: auto;
    top: auto;
  }
  top: 0px;
  position: absolute;
`;

const ModalContent = styled.div`
  padding: 16px;
  background-color: var(--clr-bg);
  width: 100%;
  border-radius: 12px;
  shadow-opacity: 0.18;
  shadow-radius: 4px;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  elevation: 10;
`;

const Description = styled.p`
  color: var(--clr-sec);
  font-size: 1rem;
  margin: 1rem 0;
  text-align: center;
  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const BlockedUsersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  max-height: 400px;
  overflow-y: auto;
`;

const BlockedUserCard = styled.div`
  background-color: var(--clr-prim);
  border: 2px solid var(--clr-tert);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--clr-purple);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const UserNickname = styled.span`
  color: var(--clr-sec);
  font-weight: 600;
  font-size: 1rem;
`;

const UserMessage = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-size: 0.85rem;
  font-style: italic;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnblockButton = styled.button`
  background-color: var(--clr-green);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #28a745;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--clr-sec);
  opacity: 0.7;
  padding: 2rem;
  font-style: italic;
`;

const LoadingMessage = styled.p`
  color: var(--clr-sec);
  text-align: center;
  margin: 2rem 0;
  font-style: italic;
`;

const BlockListModal = ({ visible, onClose }) => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBlockedUsers = () => {
    setLoading(true);
    const blockList = getBlockList();
    const blockedEntries = Array.from(blockList);

    if (blockedEntries.length === 0) {
      setBlockedUsers([]);
      setLoading(false);
      return;
    }

    // Convert entries to consistent format, handling both legacy strings and new objects
    const users = blockedEntries.map(entry => {
      if (typeof entry === 'string') {
        // Legacy format - just userId
        return {
          userId: entry,
          nickname: null,
          messageText: null
        };
      } else {
        // New format - object with userId, nickname, messageText
        return {
          userId: entry.userId,
          nickname: entry.nickname,
          messageText: entry.messageText
        };
      }
    });

    setBlockedUsers(users);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchBlockedUsers();
    }
  }, [visible]);

  const handleUnblock = (userId) => {
    try {
      removeFromBlockList(userId);
      
      // Update the UI immediately
      setBlockedUsers(prev => prev.filter(user => user.userId !== userId));
      
      toast.success('User unblocked successfully');
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error('Failed to unblock user');
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />
          <TitleText>Blocked Users</TitleText>
          <Description>Manage users you have blocked</Description>

          {loading && <LoadingMessage>Loading blocked users...</LoadingMessage>}

          {!loading && blockedUsers.length === 0 && (
            <EmptyState>
              You haven't blocked any users yet.
            </EmptyState>
          )}

          {!loading && blockedUsers.length > 0 && (
            <BlockedUsersContainer>
              {blockedUsers.map((user) => (
                <BlockedUserCard key={user.userId}>
                  <UserInfo>
                    <UserNickname>
                      {user.nickname || 'Anonymous User'}
                    </UserNickname>
                    {user.messageText && (
                      <UserMessage>
                        "{user.messageText}"
                      </UserMessage>
                    )}
                  </UserInfo>
                  <UnblockButton onClick={() => handleUnblock(user.userId)}>
                    <UserMinus size={16} />
                    Unblock
                  </UnblockButton>
                </BlockedUserCard>
              ))}
            </BlockedUsersContainer>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default BlockListModal;